<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { computed, onMounted, ref } from 'vue'

import type { AdminPagedResponse, TrashEntityType, TrashItemDTO } from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { adminService } from '@/services/adminService'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { t, d } = useI18n()
const { setPageTitle } = useBreadcrumbs()
const toast = useAppToast()

const entityType = ref<TrashEntityType>('event')
const items = ref<TrashItemDTO[]>([])
const totalCount = ref(0)
const page = ref(1)
const pageSize = ref(20)
const isLoading = ref(false)
const restoringItem = ref<{ entityType: TrashEntityType; id: number } | null>(null)
const error = ref<string | null>(null)

const entityOptions = computed(() => [
  { label: t('pages.admin.trash.entityTypes.event'), value: 'event' as const },
  { label: t('pages.admin.trash.entityTypes.registration'), value: 'registration' as const },
  { label: t('pages.admin.trash.entityTypes.eventInvite'), value: 'eventInvite' as const },
  { label: t('pages.admin.trash.entityTypes.feeSet'), value: 'feeSet' as const },
  { label: t('pages.admin.trash.entityTypes.group'), value: 'group' as const },
  { label: t('pages.admin.trash.entityTypes.rule'), value: 'rule' as const },
  { label: t('pages.admin.trash.entityTypes.workspace'), value: 'workspace' as const },
])
const columns = computed<TableColumn<TrashItemDTO>[]>(() => [
  { accessorKey: 'label', header: t('pages.admin.trash.columns.label') },
  { accessorKey: 'workspaceName', header: t('pages.admin.trash.columns.scope') },
  { accessorKey: 'deletedAt', header: t('pages.admin.trash.columns.deletedAt') },
  { accessorKey: 'deletedBy', header: t('pages.admin.trash.columns.deletedBy') },
  { accessorKey: 'relatedCount', header: t('pages.admin.trash.columns.related') },
  { id: 'actions', header: t('pages.admin.trash.columns.actions') },
])

async function loadTrash() {
  isLoading.value = true
  error.value = null

  try {
    const response = await adminService.getTrash(entityType.value, {
      page: page.value,
      pageSize: pageSize.value,
    })
    const paged = response as AdminPagedResponse<TrashItemDTO>
    items.value = paged.items
    totalCount.value = paged.totalCount
  } catch (err) {
    console.error('Failed to load trash items:', err)
    items.value = []
    totalCount.value = 0
    error.value = t('pages.admin.trash.messages.loadError')
  } finally {
    isLoading.value = false
  }
}

async function changeEntityType(nextType: TrashEntityType) {
  entityType.value = nextType
  page.value = 1
  await loadTrash()
}

async function onPage(nextPage: number) {
  page.value = nextPage
  await loadTrash()
}

function formatDeletedAt(value?: string | null) {
  if (!value) {
    return '—'
  }

  return d(new Date(value), 'datetime')
}

function isRestoring(item: TrashItemDTO) {
  return restoringItem.value?.entityType === item.entityType && restoringItem.value?.id === item.id
}

async function confirmRestore(item: TrashItemDTO) {
  const accepted = await appConfirm({
    message: t('pages.admin.trash.dialog.confirmRestore', { name: item.label }),
    title: t('pages.admin.trash.dialog.confirmRestoreHeader'),
    cancelLabel: t('pages.admin.trash.actions.cancel'),
    confirmLabel: t('pages.admin.trash.actions.restore'),
    confirmIcon: 'i-lucide-history',
  })
  if (accepted) await restoreItem(item)
}

async function restoreItem(item: TrashItemDTO) {
  restoringItem.value = { entityType: item.entityType, id: item.id }

  try {
    await adminService.restoreTrashItem(item.entityType, item.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.admin.trash.messages.restoreSuccess', { name: item.label }),
      duration: 3000,
    })
    await loadTrash()
  } catch (err) {
    console.error('Failed to restore trash item:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description:
        err instanceof Error ? err.message : t('pages.admin.trash.messages.restoreError'),
      duration: 5000,
    })
  } finally {
    restoringItem.value = null
  }
}

onMounted(() => {
  setPageTitle(t('pages.admin.trash.title'))
  loadTrash()
})
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">{{ t('pages.admin.trash.title') }}</h1>
        <p class="text-muted">{{ t('pages.admin.trash.subtitle') }}</p>
      </div>
      <UButton
        icon="i-lucide-refresh-cw"
        :loading="isLoading"
        @click="loadTrash"
        variant="outline"
      />
    </div>

    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <UCard>
      <template #default>
        <div class="mb-4 flex flex-wrap gap-2">
          <UButton
            v-for="option in entityOptions"
            :key="option.value"
            :label="option.label"
            :variant="entityType !== option.value ? 'outline' : 'solid'"
            :color="toAppColor(entityType === option.value ? 'primary' : 'secondary')"
            size="sm"
            @click="changeEntityType(option.value)"
          />
        </div>

        <AppDataTableShell :data="items" :columns="columns" :loading="isLoading">
          <template #empty>
            <div class="py-8 text-center text-muted">
              {{ t('pages.admin.trash.empty') }}
            </div>
          </template>

          <template #label-cell="{ row }">
            <div class="font-medium">{{ row.original.label }}</div>
            <div v-if="row.original.secondaryLabel" class="text-sm text-muted">
              {{ row.original.secondaryLabel }}
            </div>
          </template>

          <template #workspaceName-cell="{ row }">
            <div v-if="row.original.workspaceName">{{ row.original.workspaceName }}</div>
            <div v-if="row.original.eventName" class="text-sm text-muted">
              {{ row.original.eventName }}
            </div>
            <span v-if="!row.original.workspaceName && !row.original.eventName">—</span>
          </template>

          <template #deletedAt-cell="{ row }">
            {{ formatDeletedAt(row.original.deletedAt) }}
          </template>

          <template #deletedBy-cell="{ row }">{{ row.original.deletedBy || '—' }}</template>

          <template #relatedCount-cell="{ row }">
            <span
              v-if="row.original.relatedCount !== null && row.original.relatedCount !== undefined"
            >
              {{ row.original.relatedCount }}
              <span v-if="row.original.relatedLabel" class="text-muted">
                {{ t(row.original.relatedLabel) }}
              </span>
            </span>
            <span v-else>—</span>
          </template>

          <template #actions-cell="{ row }">
            <UButton
              icon="i-lucide-history"
              size="sm"
              :label="t('pages.admin.trash.actions.restore')"
              :loading="isRestoring(row.original)"
              @click="confirmRestore(row.original)"
            />
          </template>
        </AppDataTableShell>
        <div v-if="totalCount > pageSize" class="mt-3 flex justify-end">
          <UPagination
            :page="page"
            :total="totalCount"
            :items-per-page="pageSize"
            @update:page="onPage"
          />
        </div>
      </template>
    </UCard>
  </div>
</template>
